import { Eye, EyeOff } from "@app/ui";
import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { useCalendarsStore, useSettingsStore, useThemeStore } from "../../stores/index.js";
import { resolvedCalendarColor } from "../entity-colors.js";
import { orderByIds } from "../../lib/apply-order.js";
import { useI18n } from "../../lib/useI18n.js";
import { useDragClickGuard } from "../../lib/useDragClickGuard.js";
import { Reorder, useDragControls } from "../../lib/motion/index.js";
import { SidebarSection } from "../sidebar/SidebarSection.js";
import { useSidebarReorderProps } from "../sidebar/useSidebarReorderProps.js";

/** Sidebar calendar list inside a collapsible (persisted) section. Clicking a
 *  row toggles its visibility (`hidden`); rows are drag-to-reorder (order
 *  persists + backed up). */
export function CalendarList({ grip, onTitlePointerDown }: { grip?: ReactNode; onTitlePointerDown?: (e: ReactPointerEvent) => void } = {}) {
  const { t } = useI18n();
  const calendars = useCalendarsStore((s) => s.calendars);
  const themeConfig = useThemeStore((s) => s.config);
  const setHidden = useCalendarsStore((s) => s.setHidden);
  const sectionCollapsed = useSettingsStore((s) => s.sidebarCollapsed.calendars);
  const setSidebarCollapsed = useSettingsStore((s) => s.setSidebarCollapsed);
  const calendarOrder = useSettingsStore((s) => s.calendarOrder);
  const setCalendarOrder = useSettingsStore((s) => s.setCalendarOrder);
  const guard = useDragClickGuard();
  const reorderProps = useSidebarReorderProps();
  if (calendars.length === 0) return null;

  const ordered = orderByIds(calendars, calendarOrder, (c) => c.id);

  return (
    <SidebarSection
      title={t("common.calendars")}
      manageTo="/settings/organize"
      grip={grip}
      onTitlePointerDown={onTitlePointerDown}
      collapsed={sectionCollapsed}
      onToggle={() => void setSidebarCollapsed("calendars", !sectionCollapsed)}
    >
      <Reorder.Group as="div" axis="y" values={ordered.map((c) => c.id)} onReorder={(ids) => void setCalendarOrder(ids as string[])}>
        {ordered.map((calendar) => (
          <CalendarItem
            key={calendar.id}
            calendar={calendar}
            swatchHex={resolvedCalendarColor(themeConfig, calendars, calendar.id)}
            guard={guard}
            reorderProps={reorderProps}
            onToggleHidden={() => void setHidden(calendar.id, !calendar.hidden)}
          />
        ))}
      </Reorder.Group>
    </SidebarSection>
  );
}

type Calendar = ReturnType<typeof useCalendarsStore.getState>["calendars"][number];

function CalendarItem({
  calendar,
  swatchHex,
  guard,
  reorderProps,
  onToggleHidden,
}: {
  calendar: Calendar;
  swatchHex: string | null;
  guard: ReturnType<typeof useDragClickGuard>;
  reorderProps: ReturnType<typeof useSidebarReorderProps>;
  onToggleHidden: () => void;
}) {
  const { t } = useI18n();
  const controls = useDragControls();
  const stateText = calendar.hidden ? t("sidebar.hiddenClickShow") : t("sidebar.shownClickHide");
  return (
    <Reorder.Item
      as="div"
      value={calendar.id}
      dragListener={false}
      dragControls={controls}
      onDragStart={guard.onDragStart}
      onDragEnd={guard.onDragEnd}
      {...reorderProps}
    >
      <button
        type="button"
        onClick={() => { if (guard.suppressClick()) return; onToggleHidden(); }}
        aria-pressed={!calendar.hidden}
        aria-label={t("sidebar.calendarState", { name: calendar.name, state: stateText })}
        className={`flex w-full items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent ${calendar.hidden ? "opacity-50" : ""}`}
        title={calendar.hidden ? t("sidebar.hiddenTitle") : t("sidebar.shownTitle")}
      >
        <span className="h-2.5 w-2.5 shrink-0 rounded-full border" style={{ background: swatchHex ?? "transparent" }} />
        <span
          title={t("sidebar.dragCalendar")}
          onPointerDown={(e) => controls.start(e)}
          className="min-w-0 truncate text-left cursor-grab touch-none active:cursor-grabbing"
        >
          {calendar.name}
        </span>
        <span aria-hidden className="min-w-0 flex-1" />
        {calendar.hidden
          ? <EyeOff className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          : <Eye className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>
    </Reorder.Item>
  );
}
